import type { RawCustomField, RawProductAttribute } from "@graphql/generated/types";
import type { CustomAttributes } from "@lib/enums/ProductAttributes";
import { Attributes } from "@lib/enums/ProductAttributes";
import { hexToRgbCss } from "@ecco/ui/hex-to-rgb";

export const getProductAttributeValue = (
    attributes: RawProductAttribute[] | RawCustomField[],
    name: Attributes | CustomAttributes,
    locale?: string
) => {
    const value = attributes?.find((attr) => attr.name === name)?.value || {};
    return locale ? value[locale] : value;
};

export const getProductAttributeValueByNames = (
    attributes: RawProductAttribute[] | RawCustomField[] | undefined,
    names: string[],
    locale?: string,
    fbStory?: boolean
): string | undefined => {
    for (const name of names) {
        const raw = attributes?.find((attr) => attr.name === name)?.value;
        if (raw === undefined || raw === null) continue;
        if (typeof raw === "string") {
            if (raw.trim()) return raw.trim();
            continue;
        }
        if (typeof raw === "object") {
            const obj = raw as Record<string, unknown>;
            const v = locale ? obj[locale] : undefined;

            if (v !== undefined && v !== null && v !== "" && typeof v === "string") return v;
            const en = obj.en ?? obj["en-GB"] ?? obj["en-US"];

            if (en !== undefined && en !== null && en !== "" && typeof en === "string") return en;
            if (!fbStory) {
                const first = Object.values(obj).find(
                    (x): x is string =>
                        x !== undefined && x !== null && x !== "" && typeof x === "string"
                );

                if (first !== undefined) return first;
            }
            continue;
        }
        if (typeof raw === "number" && !Number.isNaN(raw)) return String(raw);
    }
    return undefined;
};

export const getBackgroundColorValue = (attributes: RawProductAttribute[]) => {
    try {
        const backgroundPropertyValue = getProductAttributeValue(
            attributes,
            Attributes.backgroundColor
        );
        return hexToRgbCss(backgroundPropertyValue, { alpha: 1 });
    } catch {
        return hexToRgbCss("#FFF");
    }
};

export const getColorObject = (mapping: Map<any, string>, colorName: string, language: string) => {
    return Array.from(mapping.keys()).find(
        (obj) => Object.values(obj).includes(colorName) && obj.hasOwnProperty(language)
    );
};

export const getEnrichedPageTitle = (
    rawAttributes: RawProductAttribute[],
    itemName: string,
    locale: string,
    separator: string
) => {
    const dominantColor = getProductAttributeValue(rawAttributes, Attributes.dominantColor, locale);
    const pageTitle = getProductAttributeValue(rawAttributes, Attributes.pageTitle, locale);

    return `${pageTitle ?? itemName} ${separator} ${dominantColor}`;
};

export const getImageAltTag = (altTagPattern: string, imageLabel?: string) => {
    if (!imageLabel) return altTagPattern;
    const imageAlt = imageLabel.charAt(0).toUpperCase() + imageLabel.slice(1);
    return `${altTagPattern} - ${imageAlt}`;
};

export const toCamelCaseKey = (key: string) =>
    key.replace(/\s+/g, "").replace(/([a-z])([A-Z])/g, "$1$2");
